export type AppDialogAlertOptions = {
  title?: string;
  okLabel?: string;
};

export type AppDialogConfirmOptions = {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Красная кнопка подтверждения (отмена заказа и т.п.) */
  destructive?: boolean;
};

type AlertRequest = {
  kind: "alert";
  message: string;
  title?: string;
  okLabel?: string;
  resolve: () => void;
};

type ConfirmRequest = {
  kind: "confirm";
  message: string;
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  resolve: (ok: boolean) => void;
};

export type AppDialogRequest = AlertRequest | ConfirmRequest;

type Listener = (request: AppDialogRequest) => void;

let listener: Listener | null = null;
const queue: AppDialogRequest[] = [];

export function registerAppDialogListener(fn: Listener): () => void {
  listener = fn;
  while (queue.length > 0) {
    const next = queue.shift();
    if (next) fn(next);
  }
  return () => {
    if (listener === fn) listener = null;
  };
}

function enqueue(request: AppDialogRequest): void {
  if (listener) {
    listener(request);
    return;
  }
  queue.push(request);
}

function telegramShowAlert(message: string): Promise<void> | null {
  const tg = window.Telegram?.WebApp;
  if (typeof tg?.showAlert === "function") {
    return new Promise((resolve) => {
      tg.showAlert(message, resolve);
    });
  }
  return null;
}

function telegramShowConfirm(message: string): Promise<boolean> | null {
  const tg = window.Telegram?.WebApp;
  if (typeof tg?.showConfirm === "function") {
    return new Promise((resolve) => {
      tg.showConfirm(message, resolve);
    });
  }
  return null;
}

/**
 * Alert: в Telegram — нативный showAlert, в Max/web — кастомная модалка.
 */
export function showAppAlert(
  message: string,
  options?: AppDialogAlertOptions
): Promise<void> {
  const native = telegramShowAlert(message);
  if (native) return native;

  return new Promise((resolve) => {
    enqueue({
      kind: "alert",
      message,
      title: options?.title,
      okLabel: options?.okLabel,
      resolve,
    });
  });
}

/**
 * Confirm: в Telegram без кастомных лейблов — нативный showConfirm,
 * иначе (Max / destructive / свои кнопки) — кастомная модалка.
 */
export function showAppConfirm(
  message: string,
  options?: AppDialogConfirmOptions
): Promise<boolean> {
  const useNative =
    !options?.destructive &&
    !options?.confirmLabel &&
    !options?.cancelLabel &&
    !options?.title;

  if (useNative) {
    const native = telegramShowConfirm(message);
    if (native) return native;
  }

  return new Promise((resolve) => {
    enqueue({
      kind: "confirm",
      message,
      title: options?.title,
      confirmLabel: options?.confirmLabel,
      cancelLabel: options?.cancelLabel,
      destructive: options?.destructive,
      resolve,
    });
  });
}
